'use client';

import React, { createContext, useContext, useState, useEffect } from 'react';
import { useRouter } from 'next/navigation';
import axios from "axios";

interface AuthContextType {
  isAuthenticated: boolean;
  login: (token: string) => void;
  logout: () => void;
  user: any; // Add user property to the context type
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState<any>(null); // Add user state if you want to store user info
  const [hasMounted, setHasMounted] = useState(false);

  const router = useRouter();

  useEffect(() => setHasMounted(true), []);

// Fetch user info when mounted or after login
 useEffect(() => {
  if (!hasMounted) return;
  const token = localStorage.getItem('token');
  if (!token) {
    setIsAuthenticated(false);
    setUser(null);
    localStorage.removeItem("currentUser");
    return;
  }
  // Fetch user info
  axios.get(`${process.env.NEXT_PUBLIC_API_BASE_URL}/api/auth/me`, {
    headers: { Authorization: `Bearer ${token}` },
    withCredentials: true
  })
    .then(res => {
      // If user data is missing or invalid, treat as not authenticated
      if (!res.data || !res.data.id) {
        setUser(null);
        setIsAuthenticated(false);
        localStorage.removeItem("token");
        localStorage.removeItem("currentUser");
        return;
      }
      setUser(res.data);
      setIsAuthenticated(true);
      localStorage.setItem("currentUser", JSON.stringify(res.data));
    })
    .catch(() => {
      setUser(null);
      setIsAuthenticated(false);
      localStorage.removeItem("token");
      localStorage.removeItem("currentUser");
    });
}, [hasMounted]);

  // Improved login: fetch user info after setting token
  const login = (token: string) => {
    localStorage.setItem('token', token);
    setIsAuthenticated(true);
    // Fetch user info immediately after login
    axios.get(`${process.env.NEXT_PUBLIC_API_BASE_URL}/api/auth/me`, {
      headers: { Authorization: `Bearer ${token}` },
      withCredentials: true
    })
      .then(res => {
        setUser(res.data);
        localStorage.setItem("currentUser", JSON.stringify(res.data));
        router.push('/'); // Redirect after user is set
      })
      .catch(() => {
        setUser(null);
        localStorage.removeItem("currentUser");
        router.push('/'); // Still redirect, but user will be null
      });
  };

  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('currentUser');
    setIsAuthenticated(false);
    setUser(null);
    router.push('/auth/login');
  };

  return (
    <AuthContext.Provider value={{ isAuthenticated, login, logout, user }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};